import { Component, Input, SimpleChanges, OnChanges, TrackByFunction, OnInit, OnDestroy } from '@angular/core';

import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { combineLatestWith, filter, Observable, Subject, Subscription } from 'rxjs';
import {
  MultiEditSelectionStatus,
  selectMultiEditEnabled,
  selectMultiEditEntityTypeKey,
  selectMultiEditSelectionStatuses,
  selectShouldShowMultiEditCheckboxes,
  selectUIProductSidebarOpened
} from '../../store/product-view/product-view.selectors';
import {
  selectAddableReferences,
  selectCanAdd,
  selectCanAddInTheTree,
  selectCanDelete,
  selectLatestDeletedIdList,
  selectProjectEntityNotes,
  selectProjectFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import type { AddableReference } from 'src/app/core/models/productTreeActions.model';
import { LoadingActions } from 'src/app/core/store/loading/loading.actions';
import type { EntityNote } from '../../../core/models/entity-note';
import { CopyHoodToProjectService } from '../../services/copy-hood-to-project.service';
import type { Entity, TreeEntity } from '../../../core/models/entity.model';
import { EntityHelperService } from '../../../core/services/entity-helper.service';
import {
  PropertyItemWithDepth,
  selectProductTreeMessages,
  selectProductTreeMultiEditTypesAsItemsWithDepth
} from './product-tree.selectors';
import { FormControl } from '@angular/forms';
import { ConfirmDeleteDialogComponent } from '../../../project-list/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';

@Component({
  selector: 'app-product-tree',
  templateUrl: './product-tree.component.html',
  styleUrls: ['./product-tree.component.scss'],
  standalone: false
})
export class ProductTreeComponent implements OnChanges, OnInit, OnDestroy {
  @Input() projectEntity: TreeEntity | null;
  @Input() entityTypeNames: Record<string, string>;
  @Input() messageFilter: (message: TreeEntity) => boolean;

  productSidebarOpened$: Observable<boolean>;
  canDelete$: (id: string) => Observable<boolean>;
  canAdd$: (id: string, productTypeId: string) => Observable<boolean>;
  addableReferences$: (id: string) => Observable<AddableReference[]>;
  canAddInTheTree$: (productTypeId: string) => Observable<boolean>;
  feedbackMessages$: Observable<TreeEntity[]>;
  notes$: Observable<EntityNote[]>;

  id = '';
  selectedEntity: TreeEntity;
  allCollapsed: boolean;
  copyState = false;
  nodeIdToCopy = '';
  nodeKeyToCopy = '';

  treeControl = new NestedTreeControl<TreeEntity>(node => node.Children);
  dataSource = new MatTreeNestedDataSource<TreeEntity>();

  private multiEditSelectionEnabled = false;
  private projectEntityChange$: Subject<TreeEntity>;

  protected multiEditSelectionEnabled$: Observable<boolean>;
  protected multiEditSelectionModeOptions$: Observable<PropertyItemWithDepth[]>;
  protected multiEditShouldShowCheckboxes$: Observable<boolean>;
  protected multiEditSelectionModeControl: FormControl<string>;
  protected multiEditSelectionStatuses: Record<string, MultiEditSelectionStatus> = {};

  /**
   * Used as a cache of the parents of the currently selected entity. This is needed so we can highlight the parents of the
   * selected entity in the tree without having to traverse the tree on each update for each node.
   * @private
   */
  private selectedEntityParents: TreeEntity[] = [];

  private navigationSubscription: Subscription;
  private deletedNode: TreeEntity;
  private lastDeletedNodeIds$: Observable<string[]>;
  private deletedNodeSubscription: Subscription;
  private subscriptions: Subscription[] = [];

  private nodeIcons = new Map<string, Set<number>>();

  constructor(
    private route: ActivatedRoute,
    private copyHoodToProjectService: CopyHoodToProjectService,
    private entityHelper: EntityHelperService,
    private router: Router,
    private store: Store,
    private dialog: MatDialog
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.initializeComponent();
      }
    });
    this.projectEntityChange$ = new Subject<TreeEntity>();
    this.dataSource.data = [];
    this.id = this.route.snapshot.paramMap.get('id');
    this.productSidebarOpened$ = this.store.select(selectUIProductSidebarOpened);
    this.canDelete$ = (id: string) => this.store.select(selectCanDelete(id));
    this.canAdd$ = (id: string, productTypeId: string) => this.store.select(selectCanAdd(id, productTypeId));
    this.addableReferences$ = (id: string) => this.store.select(selectAddableReferences(id));
    this.canAddInTheTree$ = (productTypeId: string) => this.store.select(selectCanAddInTheTree(productTypeId));
    this.lastDeletedNodeIds$ = this.store.select(selectLatestDeletedIdList);
    this.feedbackMessages$ = this.store.select(selectProjectFeedbackMessages);
    this.notes$ = this.store.select(selectProjectEntityNotes);

    this.multiEditSelectionModeOptions$ = this.store.select(selectProductTreeMultiEditTypesAsItemsWithDepth);
    this.multiEditSelectionEnabled$ = this.store.select(selectMultiEditEnabled);
    this.multiEditShouldShowCheckboxes$ = this.store.select(selectShouldShowMultiEditCheckboxes);

    this.multiEditSelectionModeControl = new FormControl<string>('', { updateOn: 'change' });

    this.subscriptions.push(this.multiEditSelectionEnabled$.subscribe(enabled => (this.multiEditSelectionEnabled = enabled)));

    this.subscriptions.push(
      this.store.select(selectMultiEditSelectionStatuses).subscribe(statuses => (this.multiEditSelectionStatuses = statuses))
    );

    this.subscriptions.push(
      this.store
        .select(selectMultiEditEntityTypeKey)
        .pipe(combineLatestWith(this.projectEntityChange$))
        .subscribe(([entityType]) => {
          if (entityType) {
            this.multiEditSelectionModeControl.setValue(entityType, { emitEvent: false });
            this.expandSelectedMultiEditEntityTypes(entityType);
          } else {
            this.multiEditSelectionModeControl.setValue(null, { emitEvent: false });
          }
        })
    );

    this.subscriptions.push(
      this.multiEditSelectionModeControl.valueChanges.subscribe(value => {
        this.store.dispatch(ProductViewActions.multi_edit_entity_type_selected({ typeKey: value }));
      })
    );
  }

  initializeComponent() {
    this.setSelectedEntity();
    this.allCollapsed = !this.treeControl.expansionModel.isEmpty();

    /*
     * When we delete a node, we want to select its parent - we do it in this subscription
     */
    this.deletedNodeSubscription = this.lastDeletedNodeIds$
      .pipe(filter(ids => this.deletedNode && ids.includes(this.deletedNode.Id)))
      .subscribe(() => {
        const deletedNodeParent = this.deletedNode.Parent;

        // We only want to navigate to the parent of the deleted node if the node we have currently selected is the deleted node
        // or one of its children.
        let shouldNavigateToDeletedNodeParent = false;
        let traverseParent = this.selectedEntity;

        // Simple parent-tree traverse
        while (traverseParent && !shouldNavigateToDeletedNodeParent) {
          if (traverseParent.Id === this.deletedNode.Id) {
            shouldNavigateToDeletedNodeParent = true;
          }
          traverseParent = traverseParent.Parent;
        }

        // If the parent is project, we should not navigate
        if (shouldNavigateToDeletedNodeParent && deletedNodeParent.EntityTypeKey !== 'Project') {
          this.navigateToNode(deletedNodeParent.Id);
        }
        // We don't need the deleted node anymore
        this.deletedNode = null;
      });

    this.subscriptions.push(
      this.store.select(selectProductTreeMessages).subscribe(messages => {
        this.nodeIcons = this.getMessageIcons(messages);
      })
    );
  }

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectEntity']) {
      const currentEntity = changes['projectEntity'].currentValue as TreeEntity;
      if (!currentEntity) {
        this.dataSource.data = [];
        return;
      }

      // TreeControl tracks the expanded/collapsed state by object reference -
      // we need  to persist the expanded/collapsed state manually by node id due to data immutability
      const expandedNodeIds = this.treeControl.expansionModel.selected.filter(n => n && this.treeControl.isExpanded(n)).map(n => n.Id);

      this.dataSource.data = [];
      this.dataSource.data = [currentEntity];
      // Clear the expansion model - otherwise the old entity objects will remain in the tree control selection cache.
      this.treeControl.expansionModel.clear();
      this.collapseAll();

      this.treeControl.expand(currentEntity);
      for (const nodeId of expandedNodeIds) {
        const entity = this.entityHelper.getEntityInTree(currentEntity, nodeId);
        this.treeControl.expand(entity);
      }
      this.setSelectedEntity();
      this.projectEntityChange$.next(this.projectEntity);
    }
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
    this.deletedNodeSubscription?.unsubscribe();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  protected toggleMultiEdit(): void {
    this.store.dispatch(ProductViewActions.toggle_multi_edit());
  }

  /**
   * Adjust the tree node expansion states in a way that all entities of the specified type become visible.
   * @param entityTypeKey The entity type key for entities we want to make visible
   */
  private expandSelectedMultiEditEntityTypes(entityTypeKey: string): void {
    // Get the entity tree as a flattened tree
    const flattenedEntities = this.entityHelper.flattenEntity(this.dataSource.data[0]);

    // We use a Set for easy deduplication
    const nodesWithTypeAsChild = new Set<TreeEntity>();

    // For each entity where the entityTypeKey matches, add all of it's parents recursively to the nodes-to-be-expanded Set.
    flattenedEntities.forEach(entity => {
      if (entity.EntityTypeKey === entityTypeKey) {
        let parent = entity.Parent;
        while (parent) {
          nodesWithTypeAsChild.add(parent);
          parent = parent.Parent;
        }
      }
    });

    // Collapse all nodes, then open the nodes we want to open
    this.treeControl.collapseAll();
    nodesWithTypeAsChild.forEach(nodeToExpand => {
      this.treeControl.expand(nodeToExpand);
    });
  }

  showIcon(node: Entity, level: number) {
    return this.nodeIcons.get(node.Id)?.has(level);
  }

  onChangeProductSidebarOpened(event: boolean) {
    this.store.dispatch(ProductViewActions.change_ui_product_sidebar_opened({ opened: event }));
  }

  setSelectedEntity() {
    const selectedId = this.route.snapshot.queryParamMap.get('selectedId');
    if (selectedId) {
      this.selectedEntity = this.findInTreeById(this.projectEntity, selectedId);
      this.expandParents(this.selectedEntity);
      this.selectedEntityParents = this.getSelectedEntityParents();
      const selectedElement = document.getElementById(selectedId);
      if (selectedElement !== null) {
        setTimeout(() => {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }, 100);
      }
    } else {
      this.selectedEntity = null;
    }
  }

  trackBy: TrackByFunction<TreeEntity> = (index, node) => node.Id;

  treeNodeClicked(node: TreeEntity): void {
    const nodeType = node.EntityTypeKey;
    if (nodeType === 'Project') {
      return;
    } else {
      this.navigateToNode(node.Id);
    }
  }

  isSelected(entity: TreeEntity): boolean {
    if (!this.selectedEntity || this.multiEditSelectionEnabled) {
      return false;
    }
    return entity === this.selectedEntity;
  }

  isChildSelected(entity: TreeEntity): boolean {
    if (this.multiEditSelectionEnabled || !this.selectedEntity || this.isRoot(entity) || entity === this.selectedEntity) {
      return false;
    }

    return this.selectedEntityParents.includes(entity);
  }

  /*
   * isNotMessage, isMessage, hasNonMessageChildren and hasNoOrOnlyMessageChildren are functions for matTreeNodeDefWhen
   * Nodes, which type is Message are invisible.
   * Leaf nodes are those, which either has no child nodes, or all of the child nodes are messages.
   * Other non-message nodes are nested nodes.
   *
   * There are 4 functions instead of 2, as "when" predicate is undefined, when trying to add negation to the function
   */

  isNotMessage = (index: number, node: TreeEntity): boolean => node.EntityTypeKey !== 'Message';

  isMessage = (index: number, node: TreeEntity): boolean => node.EntityTypeKey === 'Message';

  hasNoOrOnlyMessageChildren = (index: number, node: TreeEntity): boolean => {
    return !node.Children || node.Children.length === 0 || node.Children.every(child => child.EntityTypeKey === 'Message');
  };

  hasNonMessageChildren = (index: number, node: TreeEntity): boolean => {
    return !!node.Children && node.Children.length > 0 && node.Children.some(child => child.EntityTypeKey !== 'Message');
  };

  isRoot(node: TreeEntity): boolean {
    return node.ParentId === null;
  }

  isRootChild(node: TreeEntity): boolean {
    return node.Parent && node.Parent.ParentId === null;
  }

  isHood(node: TreeEntity): boolean {
    return node.EntityTypeKey === EntityTypeKey.GastroFlowHood;
  }

  expandParents(node: TreeEntity): void {
    if (node !== null) {
      node = node.Parent;
    }
    while (node) {
      this.treeControl.expand(node);
      node = node.Parent;
    }
  }

  findInTreeById(selector: TreeEntity, id: string): TreeEntity {
    if (selector.Id === id) {
      return selector;
    }
    for (const child of selector.Children) {
      const candidate = this.findInTreeById(child, id);
      if (candidate !== null) {
        return candidate;
      }
    }
    return null;
  }

  allExpanded() {
    if (this.dataSource.data && this.dataSource.data[0]) {
      const flatTree = this.entityHelper.flattenEntity(this.dataSource.data[0]);
      let allExpanded = true;
      flatTree.forEach(node => {
        if (node.Children && node.Children.length > 0 && !this.treeControl.isExpanded(node)) {
          allExpanded = false;
        }
      });
      return allExpanded;
    }
    return false;
  }

  expandAll() {
    this.dataSource.data[0].Children.forEach(node => this.treeControl.expandDescendants(node));
  }

  collapseAll() {
    this.treeControl.expand(this.dataSource.data[0]);
    this.dataSource.data[0].Children.forEach(node => this.treeControl.collapseDescendants(node));
  }

  getName(entity: TreeEntity): string {
    const nameFromProperty = entity.Properties['Name']?.Value as string;
    if (nameFromProperty) {
      return nameFromProperty;
    }

    return this.entityTypeNames[entity.EntityTypeKey] || entity.EntityTypeKey;
  }

  addNode(node: TreeEntity, ref: AddableReference) {
    const defaultValues = [];
    this.store.dispatch(LoadingActions.loading_start());
    this.store.dispatch(
      ProjectDataActions.create_entity_start({
        projectId: this.id,
        entityId: node.Id,
        productTypeId: ref.ProductTypeId,
        productName: ref.ProductName,
        defaultValues
      })
    );
  }

  deleteNode(node: TreeEntity) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, { data: node });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(LoadingActions.loading_start());
        this.store.dispatch(ProjectDataActions.delete_entity_start({ entityIdList: [node.Id] }));
        this.deletedNode = node;
      }
    });
  }

  copyNode(node: TreeEntity) {
    this.nodeIdToCopy = node.Id;
    this.nodeKeyToCopy = node.EntityTypeKey;
    this.copyState = true;
  }

  pasteNode(node: TreeEntity) {
    this.store.dispatch(LoadingActions.loading_start());
    this.store.dispatch(ProjectDataActions.copy_entity_start({ entityId: this.nodeIdToCopy, parentId: node.Id }));
    this.cancelCopy();
  }

  cancelCopy() {
    this.nodeIdToCopy = '';
    this.nodeKeyToCopy = '';
    this.copyState = false;
  }

  openCopyToProjectDialog(hood: Entity): void {
    this.copyHoodToProjectService.copyToProject(hood);
  }

  protected multiEditCheckboxStateChange(item: TreeEntity, checked: boolean): void {
    const selectionEntityType = this.multiEditSelectionModeControl.value;
    if (item.EntityTypeKey === selectionEntityType) {
      this.store.dispatch(ProductViewActions.multi_edit_entity_selection_changed({ entityId: item.Id, selected: checked }));
    } else {
      const entities = this.entityHelper.getEntitiesOfTypeFromEntityTree(item, selectionEntityType);
      this.store.dispatch(ProductViewActions.multi_edit_entity_selection_changed({ entityId: entities.map(e => e.Id), selected: checked }));
    }
  }

  private navigateToNode(nodeId: string): void {
    this.router.navigate(['.'], { queryParams: { selectedId: nodeId }, relativeTo: this.route });
  }

  private getSelectedEntityParents(): TreeEntity[] {
    if (!this.selectedEntity) {
      return [];
    }

    const result: TreeEntity[] = [];

    let parent = this.selectedEntity.Parent;

    while (parent) {
      result.push(parent);
      parent = parent.Parent;
    }

    return result;
  }

  /**
   * This function returns a Map with entity ids (keys) and sets of feedback message levels (values).
   * When entity (in the product tree) has a feedback message, it and its parent entities should show the corresponding icon after the entity name (up to the hood level, but not to the project level).
   * If there is multiple feedback messages with the same level, one icon is enough.
   */
  addMessageToIconCollection(selector: TreeEntity, iconsCollection: Map<string, Set<number>>, level: number) {
    if (selector.ParentId !== null) {
      if (iconsCollection.has(selector.Id)) {
        const oldValue = iconsCollection.get(selector.Id);
        oldValue.add(level);
        iconsCollection.set(selector.Id, oldValue);
      } else {
        const newValue = new Set<number>();
        newValue.add(level);
        iconsCollection.set(selector.Id, newValue);
      }
      if (selector.Parent.EntityTypeKey === EntityTypeKey.Project) {
        return iconsCollection;
      } else {
        return this.addMessageToIconCollection(selector.Parent, iconsCollection, level);
      }
    } else {
      return iconsCollection;
    }
  }

  getMessageIcons(messages: TreeEntity[]): Map<string, Set<number>> {
    const result = new Map<string, Set<number>>();

    messages
      .filter(msg => {
        if (this.messageFilter) {
          return this.messageFilter(msg);
        }
        return msg;
      })
      .forEach(msg => {
        if (msg.EntityTypeKey === 'Message') {
          this.addMessageToIconCollection(msg.Parent, result, msg.Properties['Level'].Value as number);
        }

        if (msg.EntityTypeKey === 'Note') {
          this.addMessageToIconCollection(msg, result, 2);
        }
      });

    return result;
  }
}
